import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * A single-page app keeps the scroll position across a route change, so
 * without this a reader who is halfway down one page lands halfway down
 * the next. Restores the top on navigation, and honours a #hash target
 * once the destination has had a frame to mount.
 *
 * `scroll-behavior: smooth` is set globally, so a plain scrollTo would
 * animate the whole page height on every navigation. 'instant' is what
 * makes a route change feel like a new page rather than a long ride.
 */
export default function ScrollManager() {
    const { pathname, hash } = useLocation();

    useEffect(() => {
        if (hash) {
            /* The target may belong to a lazily-loaded route that has not
               painted yet. Two frames is enough for Suspense to resolve a
               chunk that is already cached, and the fallback below covers
               a cold one. */
            let tries = 0;

            const find = () => {
                const target = document.getElementById(hash.slice(1));

                if (target) {
                    target.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    return;
                }

                if (tries < 20) {
                    tries += 1;
                    requestAnimationFrame(find);
                }
            };

            requestAnimationFrame(find);
            return;
        }

        window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    }, [pathname, hash]);

    return null;
}
